import React, {useEffect, useMemo, useState} from "react";
import {useAtom} from "jotai";
import styled from "styled-components";

import {
  adverTimeBudgetColumns,
  budgetTimeDataAtom
} from "./entity/BudgetTime";
import {selBudgetTimeAdverList} from "../../services/settings/BudgetTimeAxios";
import BudgetTable from "../../components/table/BudgetTable";

function BudgetTime() {
  const [budgetTimeAdverDataState, setBudgetTimeAdverDataState] =
      useAtom(budgetTimeDataAtom);
  const [keyword, setKeyword] = useState("");
  const [isSearching, setIsSearching] = useState(true);

  const normalAdvertisers = useMemo(
      () => (budgetTimeAdverDataState?.userDtos ?? []).filter(
          advertiser => advertiser?.status === "NORMAL"
      ),
      [budgetTimeAdverDataState]
  );

  const loadBudgetTimeAdvertisers = async searchKeyword => {
    setIsSearching(true);

    try {
      const response = await selBudgetTimeAdverList({
        keyword: searchKeyword.trim()
      });

      setBudgetTimeAdverDataState(response ?? {userDtos: []});
    } catch (error) {
      console.error("시간별 예산 현황 조회 실패:", error);
      setBudgetTimeAdverDataState({userDtos: []});
    } finally {
      setIsSearching(false);
    }
  };

  useEffect(() => {
    loadBudgetTimeAdvertisers("");

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSearchKeyword = event => {
    event?.preventDefault();
    loadBudgetTimeAdvertisers(keyword);
  };

  const handleClearKeyword = () => {
    setKeyword("");
    loadBudgetTimeAdvertisers("");
  };

  const advertiserCount = normalAdvertisers.length;

  return (
      <Page>
        <PageTitle>시간별 예산 현황</PageTitle>

        <SearchCard>
          <CompactSearchHeader>
            <HeaderAccent />
            <div>
              <CardTitle>광고주 검색</CardTitle>
              <CardDescription>
                광고주명 또는 아이디로 시간별 예산 설정 현황을 조회합니다.
              </CardDescription>
            </div>
          </CompactSearchHeader>

          <SearchBody>
            <SearchForm onSubmit={handleSearchKeyword}>
              <SearchInputBox>
                <SearchIcon aria-hidden="true">
                  <svg width="16" height="16" viewBox="0 0 24 24">
                    <path
                        fill="currentColor"
                        d="m21 19-4.35-4.35a7.5 7.5 0 1 0-2 2L19 21l2-2ZM5 10.5a5.5 5.5 0 1 1 11 0 5.5 5.5 0 0 1-11 0Z"
                    />
                  </svg>
                </SearchIcon>

                <KeywordInput
                    type="text"
                    value={keyword}
                    placeholder="광고주명 및 아이디 검색"
                    disabled={isSearching}
                    onChange={event => setKeyword(event.target.value)}
                />

                {keyword && (
                    <KeywordClearButton
                        type="button"
                        aria-label="검색어 지우기"
                        title="검색어 지우기"
                        disabled={isSearching}
                        onClick={handleClearKeyword}
                    />
                )}
              </SearchInputBox>

              <SearchButton type="submit" disabled={isSearching}>
                {isSearching ? "검색 중" : "검색"}
              </SearchButton>
            </SearchForm>
          </SearchBody>
        </SearchCard>

        <ResultCard>
          <ResultHeader>
            <ResultTitleArea>
              <HeaderAccent />
              <div>
                <ResultTitleLine>
                  <CardTitle>시간별 예산 설정 현황</CardTitle>
                  <ResultCount>
                    <strong>{advertiserCount}</strong>
                    <span>건</span>
                  </ResultCount>
                </ResultTitleLine>
                <CardDescription>
                  광고주별 시간대 예산 설정 여부와 상세 정보를 확인합니다.
                </CardDescription>
              </div>
            </ResultTitleArea>
          </ResultHeader>

          <ResultBody>
            {/*<ScrollGuide>Shift + 스크롤로 좌우 이동</ScrollGuide>*/}
            <TableFrame>
              {isSearching && (
                  <LoadingOverlay>
                    <LoadingSpinner />
                    <span>시간별 예산 현황을 불러오고 있습니다.</span>
                  </LoadingOverlay>
              )}

              <BudgetTable
                  variant="schedule"
                  hideToolbar={true}
                  columns={adverTimeBudgetColumns}
                  data={normalAdvertisers}
                  style={{minHeight: 350}}
                  emptyText={
                    isSearching
                        ? "시간별 예산 현황을 조회하고 있습니다."
                        : "시간별 예산 현황 내역이 없습니다."
                  }
              />
            </TableFrame>
          </ResultBody>
        </ResultCard>
      </Page>
  );
}

export default BudgetTime;

const Page = styled.div`
  display: flex;
  width: 100%;
  box-sizing: border-box;
  flex-direction: column;
  gap: 16px;
  padding: 20px;
`;

const PageTitle = styled.h2`
  margin: 0 0 2px;
  color: #222;
  font-size: var(--font-size-22);
  font-weight: 700;
`;

const SearchCard = styled.section`
  overflow: hidden;
  border: 1px solid #e5e8eb;
  border-radius: 10px;
  background-color: #fff;
  box-shadow: 0 5px 18px rgba(31, 41, 55, 0.045);
`;

const CompactSearchHeader = styled.header`
  display: flex;
  min-height: 66px;
  box-sizing: border-box;
  align-items: center;
  gap: 11px;
  padding: 14px 20px;
  border-bottom: 1px solid #eee;
  background-color: #f9fafb;
`;

const HeaderAccent = styled.span`
  width: 4px;
  height: 34px;
  flex: 0 0 4px;
  border-radius: 2px;
  background-color: #f5811f;
`;

const CardTitle = styled.div`
  color: #222;
  font-size: var(--font-size-17);
  font-weight: 700;
`;

const CardDescription = styled.div`
  margin-top: 5px;
  color: #888;
  font-size: var(--font-size-12);
  line-height: 1.5;
`;

const SearchBody = styled.div`
  padding: 18px 20px;
  background-color: #fff;
`;

const SearchForm = styled.form`
  display: flex;
  width: 100%;
  box-sizing: border-box;
  align-items: center;
  gap: 8px;

  @media (max-width: 620px) {
    align-items: stretch;
    flex-direction: column;
  }
`;

const SearchInputBox = styled.div`
  position: relative;
  width: 100%;
  min-width: 280px;
  max-width: 440px;

  @media (max-width: 700px) {
    min-width: 0;
  }
`;

const SearchIcon = styled.span`
  position: absolute;
  top: 50%;
  left: 13px;
  z-index: 1;
  display: inline-flex;
  color: #999;
  pointer-events: none;
  transform: translateY(-50%);
`;

const KeywordInput = styled.input`
  width: 100%;
  height: 36px;
  box-sizing: border-box;
  padding: 0 42px 0 39px;
  border: 1px solid #e5e5e5;
  border-radius: 6px;
  background-color: #fff;
  color: #444;
  font-size: var(--font-size-14);
  outline: none;
  transition: border-color 0.15s ease, background-color 0.15s ease;

  &:hover:not(:disabled) {
    border-color: #f1d4c2;
  }

  &:focus {
    border-color: #f5811f;
  }

  &:disabled {
    background-color: #f5f5f5;
    color: #999;
    cursor: not-allowed;
  }

  &::placeholder {
    color: #aaa;
  }
`;

const KeywordClearButton = styled.button`
  position: absolute;
  top: 50%;
  right: 10px;
  width: 22px;
  height: 22px;
  padding: 0;
  border: 0;
  border-radius: 50%;
  background-color: #f1f1f1;
  cursor: pointer;
  transform: translateY(-50%);

  &::before,
  &::after {
    position: absolute;
    top: 50%;
    left: 50%;
    width: 9px;
    height: 1px;
    background-color: #888;
    content: "";
  }

  &::before {
    transform: translate(-50%, -50%) rotate(45deg);
  }

  &::after {
    transform: translate(-50%, -50%) rotate(-45deg);
  }

  &:hover:not(:disabled) {
    background-color: #fff4e8;
  }

  &:hover:not(:disabled)::before,
  &:hover:not(:disabled)::after {
    background-color: #f5811f;
  }

  &:disabled {
    cursor: not-allowed;
    opacity: 0.55;
  }
`;

const SearchButton = styled.button`
  min-width: 72px;
  height: 36px;
  padding: 0 14px;
  border: 1px solid #f5811f;
  border-radius: 6px;
  background-color: #fff4e8;
  color: #f5811f;
  font-size: var(--font-size-14);
  font-weight: 600;
  cursor: pointer;

  &:hover:not(:disabled) {
    background-color: #ffe8d2;
  }

  &:disabled {
    border-color: #ddd;
    background-color: #f5f5f5;
    color: #aaa;
    cursor: not-allowed;
  }

  @media (max-width: 620px) {
    width: 100%;
  }
`;

const ResultCard = styled.section`
  overflow: hidden;
  border: 1px solid #e5e8eb;
  border-radius: 10px;
  background-color: #fff;
  box-shadow: 0 5px 18px rgba(31, 41, 55, 0.045);
`;

const ResultHeader = styled.header`
  display: flex;
  min-height: 74px;
  box-sizing: border-box;
  align-items: center;
  justify-content: space-between;
  padding: 16px 20px;
  border-bottom: 1px solid #eee;
  background-color: #f9fafb;
`;

const ResultTitleArea = styled.div`
  display: flex;
  min-width: 0;
  align-items: center;
  gap: 11px;
`;

const ResultTitleLine = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const ResultCount = styled.div`
  display: inline-flex;
  min-height: 25px;
  align-items: center;
  gap: 4px;
  padding: 0 9px;
  border: 1px solid #f1d4c2;
  border-radius: 13px;
  background-color: #fff4e8;

  & strong {
    color: #f5811f;
    font-size: var(--font-size-13);
  }

  & span {
    color: #888;
    font-size: var(--font-size-11);
  }
`;

const ResultBody = styled.div`
  padding: 14px 20px 20px;
`;

const ScrollGuide = styled.div`
  margin-bottom: 8px;
  color: #999;
  font-size: var(--font-size-12);
  text-align: right;
`;

const TableFrame = styled.div`
  position: relative;
  min-height: 350px;
  overflow: hidden;
  border: 1px solid #e5e8eb;
  border-radius: 8px;
  background-color: #fff;

  & > div,
  & > section,
  & > section > div:last-child,
  .InovuaReactDataGrid,
  .inovua-react-toolkit-table {
    margin: 0;
    border: 0 !important;
    border-radius: 0 !important;
    box-shadow: none !important;
  }
`;

const LoadingOverlay = styled.div`
  position: absolute;
  inset: 0;
  z-index: 10;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 9px;
  background-color: rgba(255, 255, 255, 0.78);
  color: #777;
  font-size: var(--font-size-13);
  backdrop-filter: blur(1px);
`;

const LoadingSpinner = styled.span`
  width: 18px;
  height: 18px;
  box-sizing: border-box;
  border: 2px solid #f3d5c0;
  border-top-color: #f5811f;
  border-radius: 50%;
  animation: budget-time-spin 0.7s linear infinite;

  @keyframes budget-time-spin {
    to {
      transform: rotate(360deg);
    }
  }
`;
